import React, { useState, useEffect } from "react";
import { X, Clock, Banknote, AlertCircle, Loader2 } from "lucide-react";
import { useShiftOperations, useActiveShift } from "../../hooks/useShift";
import {
  parseNumberFromSeparator,
  formatNumberWithSeparator,
} from "../../utils/numberFormat";
import { formatCurrency } from "../../utils/salesUtils";
import {
  formatShiftDuration,
  calculateShiftCashFlow,
} from "../../utils/shiftUtils";

export default function ShiftModal({
  isOpen,
  onClose,
  onOpenShift,
  onCloseShift,
}) {
  const [openingCash, setOpeningCash] = useState("");
  const [closingCash, setClosingCash] = useState("");
  const [notes, setNotes] = useState("");
  const [formErrors, setFormErrors] = useState([]);

  const { openShift, closeShift, loading, error } = useShiftOperations();
  const { activeShift, hasActiveShift, refreshActiveShift } = useActiveShift();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setOpeningCash("");
      setClosingCash("");
      setNotes("");
      setFormErrors([]);
      refreshActiveShift();
    }
  }, [isOpen, refreshActiveShift]);

  if (!isOpen) return null;

  const isShiftOpen = hasActiveShift && activeShift?.status === "OPEN";
  const cashFlow = activeShift ? calculateShiftCashFlow(activeShift) : null;

  const handleOpeningCashChange = (e) => {
    const value = e.target.value.replace(/\./g, "");
    if (value === "" || /^\d+$/.test(value)) {
      setOpeningCash(value ? formatNumberWithSeparator(value) : "");
    }
  };

  const handleClosingCashChange = (e) => {
    const value = e.target.value.replace(/\./g, "");
    if (value === "" || /^\d+$/.test(value)) {
      setClosingCash(value ? formatNumberWithSeparator(value) : "");
    }
  };

  const validateOpeningForm = () => {
    const errors = [];
    const cashAmount = parseNumberFromSeparator(openingCash);

    if (!openingCash) {
      errors.push("Modal awal kasir harus diisi");
    } else if (cashAmount < 0) {
      errors.push("Modal awal tidak boleh negatif");
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const validateClosingForm = () => {
    const errors = [];
    const cashAmount = parseNumberFromSeparator(closingCash);

    if (!closingCash) {
      errors.push("Kas tutup harus diisi");
    } else if (cashAmount < 0) {
      errors.push("Kas tutup tidak boleh negatif");
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleOpenShift = async () => {
    if (!validateOpeningForm()) return;

    try {
      const shiftData = {
        opening_cash: parseNumberFromSeparator(openingCash),
        notes: notes.trim() || undefined,
      };

      const newShift = await openShift(shiftData);
      await refreshActiveShift();

      if (onOpenShift) {
        onOpenShift(newShift);
      }

      onClose();
    } catch (error) {
      console.error("Error opening shift:", error);
      setFormErrors([error.message || "Gagal membuka shift"]);
    }
  };

  const handleCloseShift = async () => {
    if (!validateClosingForm() || !activeShift) return;

    try {
      const closingData = {
        closing_cash: parseNumberFromSeparator(closingCash),
        notes: notes.trim() || undefined,
      };

      const closedShift = await closeShift(activeShift.id, closingData);
      await refreshActiveShift();

      if (onCloseShift) {
        onCloseShift(closedShift);
      }

      onClose();
    } catch (error) {
      console.error("Error closing shift:", error);
      setFormErrors([error.message || "Gagal menutup shift"]);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-md modal-panel-enter">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              {isShiftOpen ? "Tutup Shift" : "Buka Shift"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Error Display */}
          {(formErrors.length > 0 || error) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <ul className="text-sm text-red-700">
                {formErrors.map((err, idx) => (
                  <li key={idx}>• {err}</li>
                ))}
                {error && !formErrors.length && <li>• {error}</li>}
              </ul>
            </div>
          )}

          {isShiftOpen && activeShift ? (
            <div className="space-y-6">
              {/* Shift Summary */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-3">
                  Ringkasan Shift
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Dimulai</span>
                    <span className="font-medium">
                      {new Date(activeShift.started_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Modal Awal</span>
                    <span className="font-medium">
                      {formatCurrency(activeShift.opening_cash)}
                    </span>
                  </div>

                  {cashFlow && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-700">Total Penjualan</span>
                        <span className="font-medium">
                          {formatCurrency(cashFlow.total_sales)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-700">Total Transaksi</span>
                        <span className="font-medium">
                          {cashFlow.transaction_count} transaksi
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-700">Kas Masuk</span>
                        <span className="font-medium">
                          {formatCurrency(cashFlow.total_cash_in)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-700">Kas Keluar</span>
                        <span className="font-medium">
                          {formatCurrency(cashFlow.total_cash_out)}
                        </span>
                      </div>
                      <hr className="border-blue-200" />
                      <div className="flex justify-between font-medium">
                        <span className="text-blue-900">Kas Harusnya</span>
                        <span
                          className={`${cashFlow.expected_cash >= 0 ? "text-green-700" : "text-red-700"}`}>
                          {formatCurrency(Math.abs(cashFlow.expected_cash))}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Closing Cash Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kas Tutup <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0"
                  value={closingCash}
                  onChange={handleClosingCashChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              {/* Notes Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catatan
                </label>
                <textarea
                  placeholder="Catatan penutupan shift..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  disabled={loading}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Opening Shift Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modal Awal <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0"
                  value={openingCash}
                  onChange={handleOpeningCashChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                />
              </div>

              {/* Notes Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catatan
                </label>
                <textarea
                  placeholder="Catatan pembukaan shift..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  disabled={loading}
                />
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            disabled={loading}>
            Batal
          </button>
          <button
            onClick={isShiftOpen ? handleCloseShift : handleOpenShift}
            className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            disabled={loading}>
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isShiftOpen ? "Menutup..." : "Membuka..."}
              </>
            ) : isShiftOpen ? (
              "Tutup Shift"
            ) : (
              "Buka Shift"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
